<template>
  <div class="container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex align-items-center">
        <h4 class="mb-0 mr-3">{{ $t("COMMON.TRANSFERS") }}</h4>
        <div v-if="loading" class="text-muted">
          <i class="fas fa-spinner fa-spin"></i>
          {{ $t("COMMON.LOADING") }}
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <div class="card shadow-sm">
          <div class="card-body">
            <h5 class="text-muted text-uppercase mb-1">
              {{ $t("WEEKLY_REPORTS.TRANSFER_IN") }}
            </h5>
            <div class="h4 font-weight-bold mb-2">
              {{ $formatCurrency(weeklyReport.transfer_in_total || 0) }}
            </div>
            <div class="text-muted small">
              {{ $t("WEEKLY_REPORTS.TRANSFER_IN_WITH_TAXES") }}:
              <span class="font-weight-bold">
                {{ $formatCurrency(weeklyReport.transfer_in_with_taxes || weeklyReport.transfer_in_total || 0) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card shadow-sm">
          <div class="card-body">
            <h5 class="text-muted text-uppercase mb-1">
              {{ $t("WEEKLY_REPORTS.TRANSFER_OUT") }}
            </h5>
            <div class="h4 font-weight-bold mb-2">
              {{ $formatCurrency(weeklyReport.transfer_out_total || 0) }}
            </div>
            <div class="text-muted small">
              {{ $t("WEEKLY_REPORTS.TRANSFER_OUT_WITH_TAXES") }}:
              <span class="font-weight-bold">
                {{ $formatCurrency(weeklyReport.transfer_out_with_taxes || weeklyReport.transfer_out_total || 0) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 mb-4">
        <h5 class="mb-2">{{ $t("WEEKLY_REPORTS.TRANSFER_IN") }}</h5>
        <el-table
          :data="inboundTransfers"
          header-row-class-name="thead-light"
          class="table-responsive align-items-center table-flush"
          v-loading="loading"
        >
          <div slot="empty" class="text-center text-muted py-4">
            {{ $t("WEEKLY_REPORTS.NO_TRANSFERS") }}
          </div>

          <el-table-column
            :label="$t('COMMON.NUMBER')"
            min-width="180"
          >
            <template v-slot="{ row }">
              <a
                class="text-primary"
                style="cursor: pointer"
                @click="viewTransfer(row)"
              >
                {{ row.seq_number || row.code || row.id }}
              </a>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.SOURCE')"
            min-width="200"
          >
            <template v-slot="{ row }">
              {{ row.sourceEstablishment?.company_name || row.source_establishment_id }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('ESTABLISHMENT_TRANSFERS.TRANSFER_DATE')"
            prop="transfer_date"
            min-width="150"
          />

          <el-table-column
            v-for="category in rootCategories"
            :key="`in-${category}`"
            :label="`${category} (${$t('COMMON.WITH_TAXES')})`"
            min-width="180"
            align="center"
            class-name="category-column"
          >
            <template v-slot="{ row }">
              <span class="amount-text">
                {{
                  $formatCurrency(
                    (row.categories && row.categories[category.toLowerCase()]) || 0
                  )
                }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.TOTAL_WITH_TAXES')"
            min-width="160"
            align="right"
          >
            <template v-slot="{ row }">
              {{ $formatCurrency(row?.pricing?.total || 0) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <h5 class="mb-2">{{ $t("WEEKLY_REPORTS.TRANSFER_OUT") }}</h5>
        <el-table
          :data="outboundTransfers"
          header-row-class-name="thead-light"
          class="table-responsive align-items-center table-flush"
          v-loading="loading"
        >
          <div slot="empty" class="text-center text-muted py-4">
            {{ $t("WEEKLY_REPORTS.NO_TRANSFERS") }}
          </div>

          <el-table-column
            :label="$t('COMMON.NUMBER')"
            min-width="180"
          >
            <template v-slot="{ row }">
              <a
                class="text-primary"
                style="cursor: pointer"
                @click="viewTransfer(row)"
              >
                {{ row.seq_number || row.code || row.id }}
              </a>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.DESTINATION')"
            min-width="200"
          >
            <template v-slot="{ row }">
              {{ row.destinationEstablishment?.company_name || row.destination_establishment_id }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('ESTABLISHMENT_TRANSFERS.TRANSFER_DATE')"
            prop="transfer_date"
            min-width="150"
          />

          <el-table-column
            v-for="category in rootCategories"
            :key="`out-${category}`"
            :label="`${category} (${$t('COMMON.WITH_TAXES')})`"
            min-width="180"
            align="center"
            class-name="category-column"
          >
            <template v-slot="{ row }">
              <span class="amount-text">
                {{
                  $formatCurrency(
                    (row.categories && row.categories[category.toLowerCase()]) || 0
                  )
                }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.TOTAL_WITH_TAXES')"
            min-width="160"
            align="right"
          >
            <template v-slot="{ row }">
              {{ $formatCurrency(row?.pricing?.total || 0) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import { Table, TableColumn } from "element-ui";

export default {
  name: "WeeklyReportViewTransfers",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  props: {
    weeklyReport: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      transfers: [],
      rootCategories: [],
      loading: false,
      error: false,
    };
  },
  computed: {
    establishmentId() {
      return this.weeklyReport?.establishment?.id;
    },
    inboundTransfers() {
      if (!this.establishmentId) return [];
      return (this.transfers || []).filter((transfer) => {
        const destId = transfer.destination_establishment_id || transfer.destinationEstablishment?.id;
        return destId === this.establishmentId;
      });
    },
    outboundTransfers() {
      if (!this.establishmentId) return [];
      return (this.transfers || []).filter((transfer) => {
        const srcId = transfer.source_establishment_id || transfer.sourceEstablishment?.id;
        return srcId === this.establishmentId;
      });
    },
  },
  watch: {
    "weeklyReport.id": {
      handler: "loadTransfers",
      immediate: true,
    },
    "weeklyReport.starts_at": "loadTransfers",
    "weeklyReport.ends_at": "loadTransfers",
  },
  methods: {
    async loadTransfers() {
      if (!this.weeklyReport?.id || !this.weeklyReport?.establishment?.id || !this.weeklyReport.starts_at || !this.weeklyReport.ends_at) {
        return;
      }
      this.loading = true;
      this.error = false;
      try {
        const startDate = format(new Date(this.weeklyReport.starts_at), "yyyy-MM-dd HH:mm:ss");
        const endDate = format(new Date(this.weeklyReport.ends_at), "yyyy-MM-dd HH:mm:ss");

        await Promise.all([
          this.fetchCategories(),
          this.$store.dispatch("establishmentTransfers/list", {
            filter: {
              accepted_at: [startDate, endDate],
              establishment: this.weeklyReport.establishment.id,
              status: "ACCEPTED",
            },
            include:
              "sourceEstablishment,destinationEstablishment,items,items.storable,items.storable.categories",
          }),
        ]);
        const list = this.$store.getters["establishmentTransfers/list"];
        const rawTransfers = Array.isArray(list?.data)
          ? list.data
          : Array.isArray(list)
          ? list
          : [];
        this.transfers = this.processTransfersWithCategories(rawTransfers);
        this.updateTransferTotals();
      } catch (error) {
        console.error(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchCategories() {
      try {
        await this.$store.dispatch("categories/list", {
          filter: { parent: null },
        });
        const allCategories = this.$store.getters["categories/list"];
        this.rootCategories = [
          ...new Set(
            (allCategories || []).map((cat) =>
              (cat.reporting_name
                ? `${cat.reporting_name}`
                : this.$t("WEEKLY_REPORTS.OTHER")
              ).toLowerCase()
            )
          ),
        ];
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },
    processTransfersWithCategories(transfers) {
      return transfers.map((transfer) => ({
        ...transfer,
        categories: this.categorizeTransferItems(transfer),
      }));
    },
    categorizeTransferItems(transfer) {
      const items = transfer.items || [];
      const categorizedAmounts = {};
      this.rootCategories.forEach((category) => {
        categorizedAmounts[category.toLowerCase()] = 0;
      });

      items.forEach((item) => {
        if (!item.storable || !item.storable.categories) {
          return;
        }
        const itemAmount = parseFloat(item.pricing?.total || 0);
        const productCategories = item.storable.categories || [];

        let categoryKey = this.$t("WEEKLY_REPORTS.OTHER").toLowerCase();
        const productCategory = productCategories[0];
        if (productCategory) {
          categoryKey = (
            productCategory.reporting_name || this.$t("WEEKLY_REPORTS.OTHER")
          ).toLowerCase();
        }
        if (!(categoryKey in categorizedAmounts)) {
          categorizedAmounts[categoryKey] = 0;
        }
        categorizedAmounts[categoryKey] += itemAmount;
      });

      const transferTotal = parseFloat(transfer.pricing?.total || 0);
      const itemsTotal = Object.values(categorizedAmounts).reduce(
        (a, b) => a + b,
        0
      );
      if (itemsTotal > 0 && transferTotal >= 0) {
        const ratio = transferTotal / itemsTotal;
        Object.keys(categorizedAmounts).forEach((k) => {
          categorizedAmounts[k] =
            Math.round(categorizedAmounts[k] * ratio * 100) / 100;
        });
        const scaledSum = Object.values(categorizedAmounts).reduce(
          (a, b) => a + b,
          0
        );
        const residual = Math.round((transferTotal - scaledSum) * 100) / 100;
        if (residual !== 0) {
          const largest = Object.entries(categorizedAmounts).reduce(
            (a, b) => (b[1] > a[1] ? b : a)
          )[0];
          categorizedAmounts[largest] =
            Math.round((categorizedAmounts[largest] + residual) * 100) / 100;
        }
      }

      return categorizedAmounts;
    },
    updateTransferTotals() {
      const estId = this.weeklyReport.establishment?.id;
      if (!estId) return;

      const inTotal = this.transfers.reduce((sum, transfer) => {
        const destinationId = transfer.destination_establishment_id || transfer.destinationEstablishment?.id;
        if (destinationId === estId) {
          return sum + (transfer?.pricing?.total || 0);
        }
        return sum;
      }, 0);

      const outTotal = this.transfers.reduce((sum, transfer) => {
        const sourceId = transfer.source_establishment_id || transfer.sourceEstablishment?.id;
        if (sourceId === estId) {
          return sum + (transfer?.pricing?.total || 0);
        }
        return sum;
      }, 0);

      this.weeklyReport.transfer_in_total = inTotal;
      this.weeklyReport.transfer_out_total = outTotal;
      this.weeklyReport.transfer_in_with_taxes = inTotal;
      this.weeklyReport.transfer_out_with_taxes = outTotal;
      this.emitWeeklyReportUpdate();
    },
    emitWeeklyReportUpdate() {
      this.$emit("update:weeklyReport", this.weeklyReport);
    },
    viewTransfer(transfer) {
      if (!transfer?.id) return;
      this.$router.push({
        name: "View EstablishmentTransfer",
        params: { id: transfer.id },
      });
    },
  },
};
</script>
